import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import {
  LenderReportsApi,
  ReportJobAck,
  ReportJobLifecycle,
  ReportJobStatus,
  ReportType,
} from '../tokens/lender-reports.token';

/** {@code POST /api/v1/lender/reports/{type}} response wire (B6 LenderReportJobDTO). */
interface ReportJobWire {
  jobId: string;
  type: string;
  format: string | null;
  status: string;
}

/** {@code GET /api/v1/lender/reports/jobs/{jobId}} response wire. */
interface ReportJobStatusWire {
  jobId: string;
  type: string;
  format: string | null;
  status: ReportJobLifecycle;
  completedAt?: string | null;
  errorMessage?: string | null;
}

/**
 * REST-only {@link LenderReportsApi} (EM-699 / F6 — EM-711). Queues an async report job
 * against the B6 endpoint, polls its status, and streams the generated file once COMPLETED.
 * Rides the Keycloak bearer (authInterceptor).
 */
@Injectable()
export class LenderReportsRestService implements LenderReportsApi {
  private readonly http = inject(HttpClient);

  requestReport(type: ReportType, format?: string): Observable<ReportJobAck> {
    return this.http
      .post<ReportJobWire>(`${API_PATHS.LENDER_REPORTS}/${type}`, format ? { format } : {})
      .pipe(map(j => ({ jobId: j.jobId, type: j.type, format: j.format, status: j.status })));
  }

  getJob(jobId: string): Observable<ReportJobStatus> {
    return this.http
      .get<ReportJobStatusWire>(`${API_PATHS.LENDER_REPORTS}/jobs/${encodeURIComponent(jobId)}`)
      .pipe(
        map(j => ({
          jobId: j.jobId,
          type: j.type,
          format: j.format,
          status: j.status,
          completedAt: j.completedAt,
          errorMessage: j.errorMessage,
        })),
      );
  }

  downloadReport(jobId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${API_PATHS.LENDER_REPORTS}/jobs/${encodeURIComponent(jobId)}/download`, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
